import graphql__6ea4deb4647ed330a286 from "./__generated__/MailingListDetailsViewRenameMutation.graphql.ts";
import graphql__5a9873b456a76ea5ebaa from "./__generated__/MailingListDetailsViewUpdateDescriptionMutation.graphql.ts";
import graphql__2b45f1cd30220ef2a488 from "./__generated__/MailingListDetailsView_mailingList.graphql.ts";
import graphql__2c07dd7203c85e2702d7 from "./__generated__/MailingListDetailsViewArchiveMutation.graphql.ts";
import graphql__cf8ac2a76e0384caa116 from "./__generated__/MailingListDetailsViewRestoreMutation.graphql.ts";
import graphql__49162675ce565793cde3 from "./__generated__/MailingListDetailsViewRemoveContactFromListMutation.graphql.ts";import * as React from "react";
import {useContext} from "react";
import {graphql} from "react-relay";
import {useOutletContext} from "react-router";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import {useFragment, useRelayEnvironment} from "react-relay/hooks";
import Panel from "./presentational/Panel";
import EditableDataListItem from "./form/EditableDataListItem";
import EditableDataListField, {FieldDataType} from "./form/EditableDataListField";
import ActionButton from "./presentational/button/ActionButton";
import DataListItem from "./presentational/DataListItem";
import {faBoxArchive, faWavePulse, faTrash, faPlusCircle, faPencil} from "@fortawesome/pro-regular-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useErrorContext} from "../contexts/ErrorContext";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useIntl} from "react-intl";
import Table from "./presentational/table/Table";
import TableHeader from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import StatusBadge from "./presentational/StatusBadge";
import RemoveContactFromListDialog from "./dialog/RemoveContactFromListDialog";
import AddContactToMailingListDialog from "./dialog/AddContactToMailingListDialog";
import EditSubscriptionReasonDialog from "./dialog/EditSubscriptionReasonDialog";
import EditMailingListCriterionDialog from "./dialog/EditMailingListCriterionDialog";
import MailingListDynamicMembersTable from "./MailingListDynamicMembersTable";
import CabanaConfigurationContext from "../configuration/CabanaConfigurationContext";

type Props = {}

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type MailingListDetailsViewProps = Props & ConnectedProps<typeof connector>;

const handleRenameMailingListSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__6ea4deb4647ed330a286,
            variables: {
                input: input
            },
            onCompleted: () => {
                setEditingActive(false);
            },
            onError: () => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToRenameMailingList"
        }
    )
}

const handleUpdateDescriptionSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__5a9873b456a76ea5ebaa,
            variables: {
                input: input
            },
            onCompleted: () => {
                setEditingActive(false);
            },
            onError: () => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdateMailingListDescription"
        }
    )
}

const MailingListDetailsView: React.FC<MailingListDetailsViewProps> = (props) => {
    const intl = useIntl();
    const queryRef: any = useOutletContext();
    const {addError} = useErrorContext();
    const {dispatchShowModal, dispatchHideModal} = props;
    const configuration = useContext(CabanaConfigurationContext);

    const supportedLanguages = configuration.languages?.supported || ['de', 'en'];
    const languageLabels = configuration.languages?.labels || { de: 'Deutsch', en: 'English' };

    const mailingList = useFragment(
        graphql__2b45f1cd30220ef2a488,
        queryRef
    );
    const relayEnvironment = useRelayEnvironment();

    const handleArchive = () => {
        commitMutationWithErrorHandling(
            relayEnvironment,
            {
                mutation: graphql__2c07dd7203c85e2702d7,
                variables: {
                    input: { id: mailingList.id }
                },
                onCompleted: () => {},
                onError: () => {}
            },
            addError,
            {
                customErrorMessage: "errors.failedToArchiveMailingList"
            }
        )
    }

    const handleRestore = () => {
        commitMutationWithErrorHandling(
            relayEnvironment,
            {
                mutation: graphql__cf8ac2a76e0384caa116,
                variables: {
                    input: { id: mailingList.id }
                },
                onCompleted: () => {},
                onError: () => {}
            },
            addError,
            {
                customErrorMessage: "errors.failedToRestoreMailingList"
            }
        )
    }

    const showRemoveDialog = (member: any) => {
        const contactName = getContactName(member.contact) || member.contactIdentifier;
        dispatchShowModal({
            body: (
                <RemoveContactFromListDialog
                    contactIdentifier={member.contactIdentifier}
                    mailingListIdentifier={member.mailingListIdentifier}
                    mailingListName={contactName}
                    handleCloseDialog={() => {
                        dispatchHideModal();
                    }}
                />
            )
        });
    };

    const showAddContactDialog = () => {
        dispatchShowModal({
            body: (
                <AddContactToMailingListDialog
                    mailingListIdentifier={mailingList.mailingListIdentifier}
                    mailingListId={mailingList.id}
                    handleCloseDialog={() => {
                        dispatchHideModal();
                    }}
                />
            )
        });
    };

    const showEditSubscriptionReasonDialog = () => {
        dispatchShowModal({
            body: (
                <EditSubscriptionReasonDialog
                    mailingListId={mailingList.id}
                    mailingListName={mailingList.name}
                    currentSubscriptionReason={mailingList.subscriptionReason}
                    handleCloseDialog={() => {
                        dispatchHideModal();
                    }}
                />
            )
        });
    };

    const showEditCriterionDialog = () => {
        dispatchShowModal({
            body: (
                <EditMailingListCriterionDialog
                    mailingListId={mailingList.id}
                    currentCriterion={mailingList.criterion}
                    handleCloseDialog={() => {
                        dispatchHideModal();
                    }}
                />
            )
        });
    };

    // "Resubscribe" from the unsubscribed-members table removes the suppression entry via
    // removeContactFromList. For dynamic lists this re-includes the contact (it matches the
    // criterion again); resubscribeContactToList must NOT be used here as it only applies to
    // manually managed static lists and would be rejected by the aggregate.
    const handleResubscribe = (member: any) => {
        commitMutationWithErrorHandling(
            relayEnvironment,
            {
                mutation: graphql__49162675ce565793cde3,
                variables: {
                    input: {
                        mailingListIdentifier: member.mailingListIdentifier,
                        contactIdentifier: member.contactIdentifier,
                    }
                },
                onCompleted: () => {},
                onError: () => {}
            },
            addError,
            {
                customErrorMessage: intl.formatMessage({id: 'errors.failedToResubscribeContact'})
            }
        )
    };

    const isArchived = mailingList.archivedAt !== null;
    const isDynamic = mailingList.type === 'DYNAMIC';

    const formatCondition = (condition: any): string => {
        if (condition.operator === 'EXISTS') {
            return intl.formatMessage({id: 'mailingList.criterion.condition.exists'}, {attribute: condition.attributeName});
        }
        return intl.formatMessage({id: 'mailingList.criterion.condition.equals'}, {attribute: condition.attributeName, value: condition.value});
    };

    // Build default subscription reason texts for display
    const defaultSubscriptionReasonTexts: Record<string, string> = {
        de: intl.formatMessage({id: 'mailingList.subscriptionReason.defaultText.de'}, {listName: mailingList.name}),
        en: intl.formatMessage({id: 'mailingList.subscriptionReason.defaultText.en'}, {listName: mailingList.name}),
    };

    // Get current subscription reason or null
    const subscriptionReason = mailingList.subscriptionReason;
    const hasCustomSubscriptionReason = subscriptionReason !== null && (subscriptionReason.de || subscriptionReason.en);
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusStates = () => ({
        green: [intl.formatMessage({id: 'mailingList.member.status.subscribed'})],
        gray: [intl.formatMessage({id: 'mailingList.member.status.unsubscribed'})],
    });

    const formatStatus = (status: string) => {
        switch (status) {
            case 'SUBSCRIBED': return intl.formatMessage({id: 'mailingList.member.status.subscribed'});
            case 'UNSUBSCRIBED': return intl.formatMessage({id: 'mailingList.member.status.unsubscribed'});
            default: return status;
        }
    };

    const getContactName = (contact: any) => {
        if (!contact) return null;
        if (contact.teamName) {
            return contact.teamName;
        }
        if (contact.givenName || contact.familyName) {
            return `${contact.givenName || ''} ${contact.familyName || ''}`.trim();
        }
        return contact.emailAddress;
    };

    const getContactEmail = (contact: any) => {
        if (!contact) return null;
        return contact.emailAddress;
    };

    const members = mailingList.members || [];
    const unsubscribedMembers = mailingList.unsubscribedMembers || [];

    return (
        <>
            {/* Two panels side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-[4fr_1fr] gap-4">
                <Panel>
                    <dl className="divide-y divide-gray-200">
                        <EditableDataListItem
                            label={intl.formatMessage({id: 'mailingList.field.description'})}
                            fields={(<EditableDataListField fieldDataType={FieldDataType.text} name="description"/>)}
                            initialValues={{id: mailingList.id, description: mailingList.description || ''}}
                            handleSubmission={(values, setEditingActive) => handleUpdateDescriptionSubmission(values, setEditingActive, relayEnvironment, addError)}
                            disabled={isArchived}
                        >
                            {mailingList.description || <span className="text-gray-400">{intl.formatMessage({id: 'mailingList.noDescription'})}</span>}
                        </EditableDataListItem>
                        <DataListItem label={intl.formatMessage({id: 'mailingList.field.subscriptionReason'})}>
                            <div className="flex items-start justify-between group">
                                <div className="flex-1 min-w-0">
                                    {hasCustomSubscriptionReason ? (
                                        <div className="space-y-1">
                                            {supportedLanguages.map(lang => (
                                                <div key={lang} className="text-sm">
                                                    <span className="font-medium text-gray-500">{languageLabels[lang]}:</span>{' '}
                                                    <span>{(subscriptionReason as any)?.[lang] || '-'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="space-y-1">
                                            {supportedLanguages.map(lang => (
                                                <div key={lang} className="text-sm text-gray-400">
                                                    <span className="font-medium">{languageLabels[lang]}:</span>{' '}
                                                    <span>{defaultSubscriptionReasonTexts[lang]}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {!isArchived && (
                                    <button
                                        onClick={showEditSubscriptionReasonDialog}
                                        className="ml-4 text-gray-400 hover:text-gray-600 invisible group-hover:visible"
                                        title={intl.formatMessage({id: 'button.edit'})}
                                    >
                                        <FontAwesomeIcon icon={faPencil} />
                                    </button>
                                )}
                            </div>
                        </DataListItem>
                        {isDynamic && (
                            <DataListItem label={intl.formatMessage({id: 'mailingList.field.criterion'})}>
                                <div className="flex items-start justify-between group">
                                    <div className="flex-1 min-w-0 space-y-1">
                                        {(mailingList.criterion?.conditions || []).map((condition, index) => (
                                            <div key={index} className="text-sm font-mono">{formatCondition(condition)}</div>
                                        ))}
                                    </div>
                                    {!isArchived && (
                                        <button
                                            onClick={showEditCriterionDialog}
                                            className="ml-4 text-gray-400 hover:text-gray-600 invisible group-hover:visible"
                                            title={intl.formatMessage({id: 'button.edit'})}
                                        >
                                            <FontAwesomeIcon icon={faPencil} />
                                        </button>
                                    )}
                                </div>
                            </DataListItem>
                        )}
                    </dl>
                </Panel>

                <Panel>
                    <dl className="divide-y divide-gray-200">
                        <DataListItem label={intl.formatMessage({id: 'mailingList.field.type'})}>
                            <span>{intl.formatMessage({id: isDynamic ? 'mailingList.type.dynamic' : 'mailingList.type.static'})}</span>
                        </DataListItem>
                        <DataListItem label={intl.formatMessage({id: 'mailingList.field.subscribedCount'})}>
                            <span className="text-green-600 font-medium">{mailingList.subscribedCount}</span>
                        </DataListItem>
                        <DataListItem label={intl.formatMessage({id: 'mailingList.field.unsubscribedCount'})}>
                            <span className="text-gray-500">{mailingList.unsubscribedCount}</span>
                        </DataListItem>
                    </dl>
                </Panel>
            </div>

            {/* Static lists: manually managed members */}
            {!isDynamic && (
                <Panel
                    headline={intl.formatMessage({id: 'mailingList.members'})}
                    buttons={!isArchived ? (
                        <ActionButton
                            onClick={showAddContactDialog}
                            icon={faPlusCircle}
                            label={intl.formatMessage({id: 'button.addContactToList'})}
                        />
                    ) : undefined}
                >
                    {members.length === 0 ? (
                        <div className="px-6 py-6 text-gray-500 text-center">
                            {intl.formatMessage({id: 'mailingList.noMembers'})}
                        </div>
                    ) : (
                        <div className="flex-1 min-h-0 relative overflow-y-auto">
                            <Table>
                                <thead>
                                    <tr>
                                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.status'})}</TableHeader>
                                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.name'})}</TableHeader>
                                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.email'})}</TableHeader>
                                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.subscribedAt'})}</TableHeader>
                                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.unsubscribedAt'})}</TableHeader>
                                        <TableHeader></TableHeader>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member, index) => {
                                        const contact = member.contact;
                                        const contactName = getContactName(contact) || member.contactIdentifier;
                                        const contactEmail = getContactEmail(contact);

                                        return (
                                            <tr key={index} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                                <TableData>
                                                    <StatusBadge
                                                        value={formatStatus(member.status)}
                                                        states={getStatusStates()}
                                                    />
                                                </TableData>
                                                <TableData>
                                                    <span className="font-medium">{contactName}</span>
                                                </TableData>
                                                <TableData>
                                                    {contactEmail || '-'}
                                                </TableData>
                                                <TableData>
                                                    {formatDate(member.subscribedAt)}
                                                </TableData>
                                                <TableData>
                                                    {member.unsubscribedAt ? (
                                                        <span title={member.unsubscribeReason || ''}>
                                                            {formatDate(member.unsubscribedAt)}
                                                        </span>
                                                    ) : '-'}
                                                </TableData>
                                                <TableData className="text-right">
                                                    {!mailingList.archivedAt && (
                                                        <button
                                                            onClick={() => showRemoveDialog(member)}
                                                            className="invisible group-hover:visible text-gray-400 hover:text-red-600 p-2"
                                                            title={intl.formatMessage({id: 'button.removeFromList'})}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </TableData>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Panel>
            )}

            {/* Dynamic lists: computed members (criterion matches) */}
            {isDynamic && (
                <Panel headline={intl.formatMessage({id: 'mailingList.dynamicMembers'}, {count: mailingList.subscribedCount})}>
                    <MailingListDynamicMembersTable mailingListId={mailingList.id}/>
                </Panel>
            )}

            {/* Dynamic lists: unsubscribed contacts (suppression set) with the option to re-include them */}
            {isDynamic && (
                <Panel headline={intl.formatMessage({id: 'mailingList.unsubscribedMembers'}, {count: unsubscribedMembers.length})}>
                    {unsubscribedMembers.length === 0 ? (
                        <div className="px-6 py-6 text-gray-500 text-center">
                            {intl.formatMessage({id: 'mailingList.noUnsubscribedMembers'})}
                        </div>
                    ) : (
                        <div className="flex-1 min-h-0 relative overflow-y-auto">
                            <Table>
                                <thead>
                                    <tr>
                                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.name'})}</TableHeader>
                                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.email'})}</TableHeader>
                                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.unsubscribedAt'})}</TableHeader>
                                        <TableHeader></TableHeader>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unsubscribedMembers.map((member, index) => {
                                        const contact = member.contact;
                                        const contactName = getContactName(contact) || member.contactIdentifier;
                                        const contactEmail = getContactEmail(contact);

                                        return (
                                            <tr key={index} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                                <TableData>
                                                    <span className="font-medium">{contactName}</span>
                                                </TableData>
                                                <TableData>
                                                    {contactEmail || '-'}
                                                </TableData>
                                                <TableData>
                                                    {member.unsubscribedAt ? (
                                                        <span title={member.unsubscribeReason || ''}>
                                                            {formatDate(member.unsubscribedAt)}
                                                        </span>
                                                    ) : '-'}
                                                </TableData>
                                                <TableData className="text-right">
                                                    {!isArchived && (
                                                        <button
                                                            onClick={() => handleResubscribe(member)}
                                                            className="invisible group-hover:visible text-gray-400 hover:text-green-600 p-2"
                                                            title={intl.formatMessage({id: 'button.resubscribe'})}
                                                        >
                                                            <FontAwesomeIcon icon={faWavePulse} />
                                                        </button>
                                                    )}
                                                </TableData>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Panel>
            )}

            <div className="flex space-x-4 justify-end">
                {!isArchived && (
                    <ActionButton onClick={handleArchive} icon={faBoxArchive} label={intl.formatMessage({id: 'button.archiveMailingList'})}/>
                )}
                {isArchived && (
                    <ActionButton onClick={handleRestore} icon={faWavePulse} label={intl.formatMessage({id: 'button.restoreMailingList'})}/>
                )}
            </div>
        </>
    )
}

export default connector(MailingListDetailsView);
